<template>
	<v-dialog
		v-bind="dialogProps"
		:model-value="isActiveDialog"
	>
		<v-card
			class="ao-from-dialog-card"
		>

			<div
				class="from-dialog-card-header d-flex pa-6"
				:class="{'header-line': showHeaderLine}"
			>

				<div class="d-flex flex-column">
					<span class="ao-h1">{{ formTitle }}</span>
					<span
						v-if="formSubtitle"
						class="text-textMuted ao-p3-regular"
					>
						{{ formSubtitle }}
					</span>
				</div>

				<ao-icon-btn
					iconName="close"
					class="ml-auto"
					variant="text"
					@click="onCancel('header')"
				/>

			</div>
			<div
				class="from-dialog-card-content"
				:class="[
					contentClass,
				]"
			>

				<div
					v-if="isLoading"
					class="w-100 d-flex align-center justify-center"
					style="height: 8rem;"
				>
					<v-progress-circular
						indeterminate
						color="primary"
						width="3"
						size="48"
					/>
				</div>

				<template v-else-if="isLoaded">
					<v-alert
						v-for="(error, idx) in preFillingOutFormValidationErrors"
						:key="`error-${idx}`"
						:text="error"
						closable
						color="warning"
						variant="tonal"
						density="compact"
						class="mb-2 py-1 ao-p2-regular"
					/>
					<template
						v-if="[DIALOG_FORM_TYPES.SINGLE, DIALOG_FORM_TYPES.STEPS].includes(formType)"
					>
						<div
							v-if="showDevTools"
							class="position-fixed bg-white"
							style="top: 0; left: -16px; transform: translate(-100%, 0); width: 360px; height: 500px;"
						>
							<slot
								name="dev-tools"
							/>
						</div>
						<slot
							name="default"
						/>
					</template>
					<template
						v-else-if="formType === DIALOG_FORM_TYPES.MULTI"
					>
						<slot
							v-if="multiFormStep === DIALOG_FORM_MULTI_FORM_STEP_TYPES.SUBMITTING"
							name="multi-submitting"
						/>
						<slot
							v-else
							name="default"
						/>
					</template>
				</template>

				<template v-else-if="isLoadedWithError">
					<div
						class="w-100 d-flex align-center justify-center"
						style="height: 4rem;"
					>
						{{ dialogFormErrorMessage }}
					</div>
				</template>

			</div>

			<div
				v-if="formType === DIALOG_FORM_TYPES.STEPS"
				class="from-dialog-card-footer d-flex ga-4 pa-6 bg-gray-30"
			>

				<ao-btn
					v-if="stepFormIsShowPrevStepAction"
					:disabled="stepFormStepIsProcessing"
					variant="text"
					@click="$emit(DIALOG_FORM_SAVE_ACTION_EMITS['STEP:BACK'])"
				>
					{{ $t('Return') }}
				</ao-btn>

				<ao-btn
					v-if="stepFormIsLastStep"
					:loading="stepFormStepIsProcessing"
					color="primary"
					@click="$emit(DIALOG_FORM_SAVE_ACTION_EMITS['STEP:CLOSE'])"
				>
					<slot name="stepContinueButtonText">
						{{ $t('Close') }}
					</slot>
				</ao-btn>
				<ao-btn
					v-else
					:loading="stepFormStepIsProcessing"
					color="primary"
					@click="$emit(DIALOG_FORM_SAVE_ACTION_EMITS['STEP:CONTINUE'])"
				>
					<slot name="stepContinueButtonText">
						{{ $t('Continue') }}
					</slot>
				</ao-btn>

				<ao-btn
					v-if="!stepFormIsLastStep && showCancelButton"
					variant="text"
					class="ms-auto"
					@click="onCancel('footer')"
				>
					{{ $t('Cancel') }}
				</ao-btn>

			</div>
			<div
				v-else
				class="from-dialog-card-footer pa-6"
			>
				<template
					v-if="formType === DIALOG_FORM_TYPES.MULTI && multiFormStep === DIALOG_FORM_MULTI_FORM_STEP_TYPES.SUBMITTING"
				>
					<ao-btn
						color="primary"
						class="mr-6"
						@click="$emit(DIALOG_FORM_SAVE_ACTION_EMITS['CLOSE:MULTI_FORM'])"
					>
						<slot name="submitButtonTitle">
							{{ $t('Close') }}
						</slot>
					</ao-btn>
				</template>
				<template v-else>
					<template v-if="saveActionButtons.length >= 2">
						<v-menu
							:close-on-content-click="true"
						>
							<template v-slot:activator="{ props }">
								<ao-btn
									color="primary"
									class="mr-6"
									:disabled="isDisabledActions"
									:variant="isLoading ? 'plain' : undefined"
									:loading="isSubmitting"
									@click="onSubmit(saveActionButtons[0].emit)"
								>
									<slot name="submitButtonTitle">
										{{ saveActionButtons[0].text }}
										<ao-icon
											v-bind="props"
											class="ml-2 ao-h1"
											iconName="angleDown"
										/>
									</slot>
								</ao-btn>
							</template>
							<v-card
								width="203"
								class="pa-2 mt-1"
							>
								<ao-btn
									v-for="(action, idx) in saveActionButtons"
									class="w-100 mb-2"
									:color="idx === 0 ? 'primary' : 'secondary'"
									variant="flat"
									@click="onSubmit(action.emit)"
								>
									{{ action.text }}
								</ao-btn>
							</v-card>
						</v-menu>
					</template>

					<ao-btn
						v-else-if="saveActionButtons.length === 1"
						color="primary"
						class="mr-6"
						:disabled="isDisabledActions || isLoading"
						:loading="isSubmitting"
						@click="onSubmit(saveActionButtons[0].emit)"
					>
						<slot name="submitButtonTitle">
							{{ saveActionButtons[0].text }}
						</slot>
					</ao-btn>

					<slot name="footer-additional-actions"></slot>

					<ao-btn
						v-if="showCancelButton"
						variant="text"
						@click="onCancel('footer')"
					>
						{{ $t('Cancel') }}
					</ao-btn>
				</template>
			</div>

		</v-card>
	</v-dialog>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
	DIALOG_FORM_TYPES,
	DIALOG_FORM_SAVE_ACTION_EMITS,
	DIALOG_FORM_MULTI_FORM_STEP_TYPES,
	DIALOG_FORM_SAVE_ACTION_TYPES,
	ABSTRACT_DIALOG_FORM_PROPS,
	ABSTRACT_DIALOG_FORM_EMITS,
} from '@/config/dialogForm/dialogFormConfig'

const props = defineProps({
	...ABSTRACT_DIALOG_FORM_PROPS,
})

const emit = defineEmits({
	...ABSTRACT_DIALOG_FORM_EMITS,
})

const { t: $t } = useI18n()

const saveActionButtons = computed(() => {
	const saveButton = {
		text: $t('Save'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CLOSE'],
	}
	const continueButton = {
		text: $t('Continue'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CONTINUE'],
	}
	const saveAndContinueButton = {
		text: $t('Save and continue'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CONTINUE'],
	}
	const saveAndAddButton = {
		text: $t('Save and add'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CONTINUE'],
	}
	const saveAndCloseButton = {
		text: $t('Save and close'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CLOSE'],
	}
	const saveAndCreateNewButton = {
		text: $t('Create new'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CREATE_NEW'],
	}
	const copyButton = {
		text: $t('Copy'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:COPY'],
	}
	const closeButton = {
		text: $t('Close'),
		emit: DIALOG_FORM_SAVE_ACTION_EMITS['CLOSE'],
	}
	if (props.saveActionType === DIALOG_FORM_SAVE_ACTION_TYPES.ONE_MORE_STEP) {
		return [saveAndAddButton, saveAndCloseButton, saveAndCreateNewButton]
	} else if (props.saveActionType === DIALOG_FORM_SAVE_ACTION_TYPES.EDITING) {
		return [saveButton, saveAndContinueButton]
	} else if (props.saveActionType === DIALOG_FORM_SAVE_ACTION_TYPES.BINDING) {
		return [continueButton]
	} else if (props.saveActionType === DIALOG_FORM_SAVE_ACTION_TYPES.SIMPLE) {
		return [saveButton]
	} else if (props.saveActionType === DIALOG_FORM_SAVE_ACTION_TYPES.COPYING) {
		return [copyButton]
	} else if (props.saveActionType === DIALOG_FORM_SAVE_ACTION_TYPES.SHOWING) {
		return [closeButton]
	}
	return [saveButton]
})

const onSubmit = (actionType) => {
	if (props.isLoading) return 0

	if (actionType === DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CLOSE']) {
		emit(DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CLOSE'])
	} else if (actionType === DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CONTINUE']) {
		emit(DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CONTINUE'])
	} else if (actionType === DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CREATE_NEW']) {
		emit(DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:CREATE_NEW'])
	} else if (actionType === DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:COPY']) {
		emit(DIALOG_FORM_SAVE_ACTION_EMITS['SAVE:COPY'])
	} else if (actionType === DIALOG_FORM_SAVE_ACTION_EMITS['CLOSE']) {
		emit(DIALOG_FORM_SAVE_ACTION_EMITS['CLOSE'])
	}
}

const onCancel = (from) => {
	emit('cancel', {
		from,
	})
}
</script>

<style lang="scss">
.ao-from-dialog-card {
	display: flex;
	flex-direction: column;
	width: 100%;
	overflow: visible;
	height:100%;

	.header-line {
		border-bottom: 1px solid #e6eef3;
	}

	.from-dialog-card-content {
		padding: 24px;
	}

}
</style>
